import {
  North,
  NorthEast,
  NorthWest,
  South,
  SouthEast,
  SouthWest,
  West,
  East,
} from "@mui/icons-material";
import {
  PAYMENT_METHODS_API_URL,
  STATIONS_API_URL,
  STATION_IMAGES_API_URL,
} from "./apiConstants";

export const DIRECTION_ICONS = {
  N: <North />,
  E: <East />,
  S: <South />,
  W: <West />,
  NE: <NorthEast />,
  NW: <NorthWest />,
  SE: <SouthEast />,
  SW: <SouthWest />,
};

// Simple function to fetch payment methods from API
export const fetchPaymentMethods = async () => {
  try {
    const response = await fetch(PAYMENT_METHODS_API_URL);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    return {};
  }
};

// Function to fetch stations data from API
export const fetchStations = async () => {
  try {
    const response = await fetch(STATIONS_API_URL);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("✅ Stations loaded from API:", data.length, "stations");
    return data;
  } catch (error) {
    console.error("Error fetching stations:", error);
    return [];
  }
};

// Function to fetch station images from API
export const fetchStationImages = async () => {
  try {
    const response = await fetch(STATION_IMAGES_API_URL);
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    return {};
  } catch (error) {
    console.error("Error fetching station images:", error);
    return {};
  }
};

const KEYS_MAP = {
  CNG: "StationFuelTypeCNG",
  LNG: "StationFuelTypeLNG",
  "Renewable (RNG)": "StationFuelTypeRNG",
  "Box trucks": "VehicleTypesBoxTrucks",
  "Cars and vans": "VehicleTypesCarsAndVans",
  "Semi trucks": "VehicleTypesSemiTrucks",
  Operational: "StationStatus",
  "Under maintenance": "StationStatus",
  "Coming soon": "StationStatus",
  "24/7": "HoursOpenIs24H",
  // ...PAYMENT_METHODS,
};

export const filterServerData = (filters, targetArray) => {
  console.log("🔍 Filtering with:", filters);
  console.log("🔍 Total stations:", targetArray.length);

  // Return all stations if no filters are selected or all filters are empty
  // Note: vehicleSize is a string, not an array, so we need special handling
  if (
    !filters ||
    Object.entries(filters).every(([key, filter]) => {
      if (key === "vehicleSize") {
        return !filter || filter === "";
      }
      return !filter || !filter.length;
    })
  ) {
    console.log("✅ No filters applied, returning all stations");
    return targetArray;
  }

  const checkSubFilter = (station, filterKey, filterValues) => {
    return filterValues.some((value) => {
      const mappedKey = KEYS_MAP[value];

      if (!mappedKey) {
        console.warn(`No mapping found for filter key: ${value}`);
        return false;
      }

      // Handle stationStatus separately
      if (filterKey === "stationStatus") {
        if (value === "Operational") {
          const result = station[mappedKey] === "Active";
          console.log(
            `🏪 Status filter: ${value} -> ${mappedKey} = ${station[mappedKey]} -> ${result}`
          );
          return result;
        } else if (value === "Under maintenance") {
          // Data has "Under Maintenance" so compare case-insensitively
          const stationStatusLower = station[mappedKey]?.toLowerCase();
          const expectedLower = "under maintenance";
          const result = stationStatusLower === expectedLower;
          console.log(
            `🏪 Status filter: ${value} -> ${mappedKey} = "${station[mappedKey]}" (lowercase: "${stationStatusLower}") -> ${result} (expected: "${expectedLower}")`
          );
          return result;
        } else if (value === "Coming soon") {
          // Data has "Coming Soon" so compare case-insensitively
          const stationStatusLower = station[mappedKey]?.toLowerCase();
          const expectedLower = "coming soon";
          const result = stationStatusLower === expectedLower;
          console.log(
            `🏪 Status filter: ${value} -> ${mappedKey} = "${station[mappedKey]}" (lowercase: "${stationStatusLower}") -> ${result} (expected: "${expectedLower}")`
          );
          return result;
        }
        return false; // Return false if status value doesn't match any expected value
      }

      // Handle vehicle size filtering logic with hierarchical/inclusive logic
      if (filterKey === "vehicleSize") {
        const semiTrucksValue =
          station.VehicleTypesSemiTrucks?.toString().toLowerCase();
        const boxTrucksValue =
          station.VehicleTypesBoxTrucks?.toString().toLowerCase();
        const carsAndVansValue =
          station.VehicleTypesCarsAndVans?.toString().toLowerCase();

        const acceptsSemiTrucks = semiTrucksValue === "yes";
        const acceptsBoxTrucks = boxTrucksValue === "yes";
        const acceptsCarsAndVans = carsAndVansValue === "yes";

        // Hierarchical filtering logic:
        // - "Cars and vans" filter: Show ALL stations (all stations should accept cars and vans)
        if (value === "Cars and vans") {
          return true; // Show all stations
        }

        // - "Box trucks" filter: Show stations that accept Box trucks OR Semi trucks
        //   (because stations accepting Semi trucks also accept Box trucks)
        if (value === "Box trucks") {
          const result = acceptsBoxTrucks || acceptsSemiTrucks;
          console.log(
            `🚛 Vehicle filter: ${value} -> Station ${station.StationIdNumber} accepts BoxTrucks: ${acceptsBoxTrucks}, SemiTrucks: ${acceptsSemiTrucks} -> ${result}`
          );
          return result;
        }

        // - "Semi trucks" filter: Show ONLY stations that accept Semi trucks
        if (value === "Semi trucks") {
          const result = acceptsSemiTrucks;
          console.log(
            `🚛 Vehicle filter: ${value} -> Station ${station.StationIdNumber} accepts SemiTrucks: ${acceptsSemiTrucks} -> ${result}`
          );
          return result;
        }

        // Fallback for any other values (shouldn't happen, but just in case)
        console.warn(`Unknown vehicle size filter value: ${value}`);
        return false;
      }

      // Handle hours filtering logic
      if (filterKey === "hours") {
        if (value === "24/7") {
          // Handle both "yes"/"no" and "Yes"/"No" case-insensitively
          const hours24H = station.HoursOpenIs24H?.toLowerCase();
          const result = hours24H === "yes";
          console.log(
            `🕐 Hours filter: ${value} -> HoursOpenIs24H = "${station.HoursOpenIs24H}" (lowercase: "${hours24H}") -> ${result}`
          );
          console.log(
            `🕐 Station ${station.StationIdNumber} (${
              station.StationCity || "Unknown"
            }): HoursOpenIs24H = ${station.HoursOpenIs24H}`
          );
          return result;
        }
        return false;
      }

      // Handle fuel type filtering logic
      if (filterKey === "fuelType") {
        // Handle both "Yes"/"No" case-insensitively
        const fuelValue = station[mappedKey]?.toString().toLowerCase();
        const result = fuelValue === "yes";
        console.log(
          `⛽ Fuel filter: ${value} -> ${mappedKey} = "${station[mappedKey]}" (lowercase: "${fuelValue}") -> ${result} for station ${station.StationIdNumber}`
        );
        return result;
      }

      // Handle fuel source filtering logic (Renewable/RNG)
      if (filterKey === "source") {
        // Handle both "Yes"/"No" case-insensitively
        const sourceValue = station[mappedKey]?.toString().toLowerCase();
        const result = sourceValue === "yes";
        console.log(
          `🌱 Source filter: ${value} -> ${mappedKey} = "${station[mappedKey]}" (lowercase: "${sourceValue}") -> ${result} for station ${station.StationIdNumber}`
        );
        return result;
      }

      // Default logic for other filters - handle case-insensitive "Yes"
      const valueLower = station[mappedKey]?.toString().toLowerCase();
      const result = valueLower === "yes";
      console.log(
        `🔍 Default filter: ${value} -> ${mappedKey} = "${station[mappedKey]}" (lowercase: "${valueLower}") -> ${result} for station ${station.StationIdNumber}`
      );
      return result;
    });
  };

  const checkSubFilterForPayments = (station, filterValues) => {
    if (filterValues?.length === 0) return true;
    return filterValues.some((value) => {
      // Check if the station accepts this payment type (case-insensitive)
      if (!station.PaymentTypes) return false;

      // Convert PaymentTypes object values to lowercase for comparison
      const acceptedPayments = Object.values(station.PaymentTypes).map(
        (payment) => payment.toLowerCase()
      );
      const searchValue = value.toLowerCase();

      return acceptedPayments.includes(searchValue);
    });
  };

  const filterKeys = [
    "fuelType",
    "hours",
    "paymentTypes",
    "stationStatus",
    "vehicleSize",
    "source",
  ];

  const filteredStations = targetArray.filter((station) =>
    filterKeys.every((key) => {
      console.log(
        "🚀 ~ filterKeys.every ~ key:",
        key,
        "filters:",
        filters[key]
      );

      // Special handling for stationStatus
      if (key === "stationStatus") {
        // If no status filter is selected, return all stations
        if (!filters[key] || filters[key].length === 0) {
          return true;
        }
        // Otherwise check if station matches any selected status
        const matches = checkSubFilter(station, key, filters[key]);
        console.log(
          `🏪 Station ${station.StationIdNumber} status check: ${filters[key]} matches=${matches}`
        );
        return matches;
      }

      // Special handling for paymentTypes
      if (key === "paymentTypes") {
        return checkSubFilterForPayments(station, filters.paymentTypes);
      }

      // Special handling for vehicleSize (it's a string, not an array)
      if (key === "vehicleSize") {
        const vehicleSizeValue = filters[key];
        if (!vehicleSizeValue || vehicleSizeValue === "") {
          return true; // No filter selected, show all
        }
        // Call checkSubFilter with the value wrapped in an array for compatibility
        const matches = checkSubFilter(station, key, [vehicleSizeValue]);
        console.log(
          `🔍 Filter ${key}: station ${station.StationIdNumber} matches=${matches}`
        );
        return matches;
      }

      // For other filters: if empty, show all; otherwise check if station matches
      const hasFilter = filters[key]?.length > 0;
      if (!hasFilter) return true;

      const matches = checkSubFilter(station, key, filters[key]);
      console.log(
        `🔍 Filter ${key}: station ${station.StationIdNumber} matches=${matches}`
      );
      return matches;
    })
  );

  console.log(`✅ Filtered results: ${filteredStations.length} stations`);
  return filteredStations;
};

export const fetchLocationName = async (lat, lng) => {
  try {
    // Add a small delay to respect rate limits
    await new Promise((resolve) => setTimeout(resolve, 100));

    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
      {
        headers: {
          "User-Agent": "StationLocator/1.0",
        },
      }
    );

    if (!response.ok) {
      console.warn(`HTTP error! status: ${response.status}`);
      return `Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
    }

    const data = await response.json();
    if (data && data.display_name) {
      return data.display_name; // Get the readable location name
    }
    return `Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
  } catch (error) {
    console.error("Error fetching location name:", error);
    return `Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
  }
};

export const clearLocalStorage = () => {
  window?.localStorage?.removeItem("SOURCE_CITY");
  window?.localStorage?.removeItem("DESTINATION_CITY");
  window?.localStorage?.removeItem("SOURCE_CITY_META_DATA");
  window?.localStorage?.removeItem("DESTINATION_CITY_META_DATA");
  window?.localStorage?.removeItem("DESTINATIONS_METADATA");
  window?.localStorage?.removeItem("DESTINATIONS");
};

export const capitalizeEachWord = (str) => {
  return str
    ? str
        .split(" ")
        .map((word) =>
          word.includes("-")
            ? word
                .split("-")
                .map(
                  (subWord) =>
                    subWord.charAt(0).toUpperCase() +
                    subWord.slice(1).toLowerCase()
                )
                .join("-")
            : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    : "";
};
